import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Avatar,
  Box,
  Card,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TablePagination,
  TableSortLabel,
  Chip,
} from "@mui/material";
import apiService from "../../../../../service/Api";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

// ─── Types ────────────────────────────────────────────────────────────────────

interface LeaderboardUser {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  venue_count: number;
  average_venue_score: number;
}

interface UserVenue {
  id: number;
  name: string;
  venue_type?: string;
  files?: { file: string; is_cover?: boolean }[];
  suburb?: string;
  address_one?: string;
  city?: string;
  created_at: string;
}

interface VenuesApiResponse {
  results: UserVenue[];
}

interface LeaderboardResponse {
  leaderboard: LeaderboardUser[];
  total_participants: number;
}

type SortField =
  | "name"
  | "allTimeTotal"
  | "ytdRank"
  | "mtdRank"
  | "venuesThisMonth"
  | "venuesLastMonth"
  | "venuesYTD"
  | "monthChange";

type SortDirection = "asc" | "desc";

interface CombinedRow {
  id: number;
  fullName: string;
  email: string;
  allTimeTotal: number;
  venuesThisMonth: number;
  venuesLastMonth: number;
  venuesYTD: number;
  monthChange: number;
  ytdRank: number;
  mtdRank: number | null; // null = no venues this month, don't show rank
}

// ─── Component ────────────────────────────────────────────────────────────────

const VenueLeaderboard2: React.FC = () => {
  const [allTimeData, setAllTimeData] = useState<LeaderboardUser[]>([]);
  const [thisMonthMap, setThisMonthMap] = useState<Map<number, number>>(
    new Map(),
  );
  const [lastMonthMap, setLastMonthMap] = useState<Map<number, number>>(
    new Map(),
  );
  const [ytdMap, setYtdMap] = useState<Map<number, number>>(new Map());
  const [loading, setLoading] = useState(false);

  const [sortField, setSortField] = useState<SortField>("allTimeTotal");
  const [sortDir, setSortDir] = useState<SortDirection>("desc");

  const [selectedUser, setSelectedUser] = useState<LeaderboardUser | null>(
    null,
  );
  const [userVenues, setUserVenues] = useState<UserVenue[]>([]);
  const [venuesLoading, setVenuesLoading] = useState(false);
  const [modalPage, setModalPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const navigate = useNavigate();

  // ── Fetch ───────────────────────────────────────────────────────────────────

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const now = dayjs();
        const lastMonth = now.subtract(1, "month");
        const monthsYTD = Array.from(
          { length: now.month() + 1 },
          (_, i) => i + 1,
        );

        const [allTimeRes, thisMonthRes, lastMonthRes, ...ytdResponses] =
          await Promise.all([
            apiService.get<LeaderboardResponse>(
              `/api/management/dashboard/venues-leaderboard/`,
            ),
            apiService.get<LeaderboardResponse>(
              `/api/management/dashboard/venues-leaderboard/?year=${now.year()}&month=${now.month() + 1}`,
            ),
            apiService.get<LeaderboardResponse>(
              `/api/management/dashboard/venues-leaderboard/?year=${lastMonth.year()}&month=${lastMonth.month() + 1}`,
            ),
            ...monthsYTD.map((m) =>
              apiService.get<LeaderboardResponse>(
                `/api/management/dashboard/venues-leaderboard/?year=${now.year()}&month=${m}`,
              ),
            ),
          ]);

        setAllTimeData(allTimeRes.data.leaderboard ?? []);

        const buildMap = (users: LeaderboardUser[]) =>
          new Map(users.map((u) => [u.id, u.venue_count]));

        setThisMonthMap(buildMap(thisMonthRes.data.leaderboard ?? []));
        setLastMonthMap(buildMap(lastMonthRes.data.leaderboard ?? []));

        const ytd = new Map<number, number>();
        ytdResponses.forEach((res) => {
          (res.data.leaderboard ?? []).forEach((u) => {
            ytd.set(u.id, (ytd.get(u.id) ?? 0) + u.venue_count);
          });
        });
        setYtdMap(ytd);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, []);

  // ── Build rows ──────────────────────────────────────────────────────────────

  const combinedRows: CombinedRow[] = useMemo(() => {
    const rows: CombinedRow[] = allTimeData.map((user) => {
      const fullName =
        user.first_name || user.last_name
          ? `${user.first_name} ${user.last_name}`.trim()
          : user.email;

      const venuesThisMonth = thisMonthMap.get(user.id) ?? 0;
      const venuesLastMonth = lastMonthMap.get(user.id) ?? 0;

      return {
        id: user.id,
        fullName,
        email: user.email,
        allTimeTotal: user.venue_count,
        venuesThisMonth,
        venuesLastMonth,
        venuesYTD: ytdMap.get(user.id) ?? 0,
        monthChange: venuesThisMonth - venuesLastMonth,
        ytdRank: 0,
        mtdRank: null,
      };
    });

    // YTD rank — all users get one (everyone has an all-time history)
    [...rows]
      .sort((a, b) => b.venuesYTD - a.venuesYTD)
      .forEach((r, i) => {
        r.ytdRank = i + 1;
      });

    // MTD rank — only assigned to users who have venues this month
    [...rows]
      .filter((r) => r.venuesThisMonth > 0)
      .sort((a, b) => b.venuesThisMonth - a.venuesThisMonth)
      .forEach((r, i) => {
        r.mtdRank = i + 1;
      });
    // Users with venuesThisMonth === 0 keep mtdRank = null → shows "—"

    return rows;
  }, [allTimeData, thisMonthMap, lastMonthMap, ytdMap]);

  // ── Sort ────────────────────────────────────────────────────────────────────

  const sortedRows = useMemo(() => {
    return [...combinedRows].sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case "name":
          cmp = a.fullName.localeCompare(b.fullName);
          break;
        case "allTimeTotal":
          cmp = a.allTimeTotal - b.allTimeTotal;
          break;
        // nulls (no MTD activity) always go to the bottom regardless of direction
        case "mtdRank": {
          if (a.mtdRank === null && b.mtdRank === null) cmp = 0;
          else if (a.mtdRank === null) cmp = 1;
          else if (b.mtdRank === null) cmp = -1;
          else cmp = a.mtdRank - b.mtdRank;
          return sortDir === "asc" ? cmp : -cmp;
        }
        case "ytdRank":
          cmp = a.ytdRank - b.ytdRank;
          break;
        case "venuesThisMonth":
          cmp = a.venuesThisMonth - b.venuesThisMonth;
          break;
        case "venuesLastMonth":
          cmp = a.venuesLastMonth - b.venuesLastMonth;
          break;
        case "venuesYTD":
          cmp = a.venuesYTD - b.venuesYTD;
          break;
        case "monthChange":
          cmp = a.monthChange - b.monthChange;
          break;
      }
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [combinedRows, sortField, sortDir]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir(
        field === "ytdRank" || field === "mtdRank" || field === "name"
          ? "asc"
          : "desc",
      );
    }
  };

  // ── Modal ───────────────────────────────────────────────────────────────────

  const handleUserClick = useCallback(
    async (row: CombinedRow) => {
      const user = allTimeData.find((u) => u.id === row.id)!;
      setSelectedUser(user);
      setModalPage(0);
      setVenuesLoading(true);

      try {
        const now = dayjs();
        const res = await apiService.get<VenuesApiResponse>(
          `/api/venues/venue/?created_by=${user.id}&page_size=1000`,
        );
        const venues = (res.data.results ?? []).filter((v) => {
          const d = dayjs(v.created_at);
          return d.year() === now.year() && d.month() === now.month();
        });
        setUserVenues(venues);
      } catch {
        setUserVenues([]);
      } finally {
        setVenuesLoading(false);
      }
    },
    [allTimeData],
  );

  const paginatedVenues = userVenues.slice(
    modalPage * rowsPerPage,
    modalPage * rowsPerPage + rowsPerPage,
  );

  // ── UI helpers ──────────────────────────────────────────────────────────────

  const rankBadge = (rank: number | null) => {
    if (rank === null)
      return (
        <Typography variant="body2" color="text.secondary">
          —
        </Typography>
      );

    const medals: Record<number, { bg: string; color: string }> = {
      1: { bg: "#FFD700", color: "#7a5a00" },
      2: { bg: "#C0C0C0", color: "#444" },
      3: { bg: "#CD7F32", color: "#fff" },
    };
    const style = medals[rank] ?? { bg: "#f0f0f0", color: "#555" };
    return (
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 28,
          height: 28,
          borderRadius: "50%",
          bgcolor: style.bg,
          color: style.color,
          fontWeight: 700,
          fontSize: "0.75rem",
        }}
      >
        {rank}
      </Box>
    );
  };

  const changeChip = (val: number) => {
    if (val === 0)
      return (
        <Typography variant="body2" color="text.secondary">
          —
        </Typography>
      );
    return (
      <Chip
        label={val > 0 ? `+${val}` : val}
        size="small"
        sx={{
          fontWeight: 700,
          fontSize: "0.75rem",
          bgcolor: val > 0 ? "#e6f9f0" : "#fff0f0",
          color: val > 0 ? "#1a7a4a" : "#c0392b",
          border: `1px solid ${val > 0 ? "#a8e6c8" : "#f5b8b0"}`,
        }}
      />
    );
  };

  const SortCell = ({
    field,
    label,
    align = "left",
  }: {
    field: SortField;
    label: string;
    align?: "left" | "center" | "right";
  }) => (
    <TableCell align={align} sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
      <TableSortLabel
        active={sortField === field}
        direction={sortField === field ? sortDir : "asc"}
        onClick={() => handleSort(field)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <>
      <Card sx={{ p: 3, borderRadius: 3, boxShadow: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h5" fontWeight={700}>
            Venue Leaderboard
          </Typography>
          {!loading && (
            <Typography variant="body2" color="text.secondary">
              {sortedRows.length} creator{sortedRows.length !== 1 ? "s" : ""}
            </Typography>
          )}
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f7f7f7" }}>
                  <TableCell sx={{ fontWeight: 700, width: 40 }}>#</TableCell>
                  <SortCell field="name" label="Name" />
                  <SortCell
                    field="allTimeTotal"
                    label="All-Time Total"
                    align="center"
                  />
                  <SortCell field="ytdRank" label="YTD Rank" align="center" />
                  <SortCell field="mtdRank" label="MTD Rank" align="center" />
                  <SortCell
                    field="venuesYTD"
                    label="Venues YTD"
                    align="center"
                  />
                  <SortCell
                    field="venuesThisMonth"
                    label="This Month"
                    align="center"
                  />
                  <SortCell
                    field="venuesLastMonth"
                    label="Last Month"
                    align="center"
                  />
                  <SortCell
                    field="monthChange"
                    label="MoM Change"
                    align="center"
                  />
                </TableRow>
              </TableHead>

              <TableBody>
                {sortedRows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={9}
                      align="center"
                      sx={{ py: 4, color: "text.secondary" }}
                    >
                      No leaderboard data available.
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedRows.map((row, index) => (
                    <TableRow
                      key={row.id}
                      hover
                      sx={{ cursor: "pointer", height: 56 }}
                      onClick={() => handleUserClick(row)}
                    >
                      <TableCell>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          fontWeight={600}
                        >
                          {index + 1}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography fontWeight={600} variant="body2">
                          {row.fullName}
                        </Typography>
                        {row.fullName !== row.email && (
                          <Typography variant="caption" color="text.secondary">
                            {row.email}
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell align="center">
                        <Typography fontWeight={700}>
                          {row.allTimeTotal}
                        </Typography>
                      </TableCell>

                      <TableCell align="center">
                        {rankBadge(row.ytdRank)}
                      </TableCell>
                      <TableCell align="center">
                        {rankBadge(row.mtdRank)}
                      </TableCell>

                      <TableCell align="center">
                        <Typography fontWeight={600}>
                          {row.venuesYTD}
                        </Typography>
                      </TableCell>

                      <TableCell align="center">
                        <Typography>{row.venuesThisMonth}</Typography>
                      </TableCell>

                      <TableCell align="center">
                        <Typography color="text.secondary">
                          {row.venuesLastMonth}
                        </Typography>
                      </TableCell>

                      <TableCell align="center">
                        {changeChip(row.monthChange)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* ── Venue Detail Modal ── */}
      <Dialog
        open={!!selectedUser}
        onClose={() => setSelectedUser(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {selectedUser?.first_name || selectedUser?.email}&apos;s Venues This
          Month
        </DialogTitle>
        <DialogContent>
          {venuesLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : userVenues.length === 0 ? (
            <Typography
              sx={{ textAlign: "center", py: 4, color: "text.secondary" }}
            >
              No venues created this month.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f7f7f7" }}>
                    <TableCell>Image</TableCell>
                    <TableCell>Venue</TableCell>
                    <TableCell>Address</TableCell>
                    <TableCell>Type</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedVenues.map((venue) => (
                    <TableRow
                      key={venue.id}
                      hover
                      sx={{ cursor: "pointer", height: 56 }}
                      onClick={() => navigate(`/venues/view/${venue.id}`)}
                    >
                      <TableCell>
                        {venue.files?.[0]?.file ? (
                          <Avatar
                            variant="rounded"
                            src={venue.files[0].file}
                            sx={{ width: 36, height: 36 }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: 36,
                              height: 36,
                              bgcolor: "grey.200",
                              borderRadius: 1,
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight={600} variant="body2">
                          {venue.name || "Unnamed Venue"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {venue.address_one ??
                            venue.suburb ??
                            venue.city ??
                            "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {venue.venue_type || "—"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <TablePagination
            component="div"
            count={userVenues.length}
            page={modalPage}
            onPageChange={(_, p) => setModalPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setModalPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default VenueLeaderboard2;
